<template>
  <a-form-model
    v-if="loaded"
    :model="form"
    :colon="false"
    :label-col="{ span: 6 }"
    :wrapper-col="{ span: 16 }"
    :class="{ loaded }"
    class="create-or-edit-calendar-event-component"
    ref="calendarEventForm"
  >

    <a-form-model-item label="Onderwerp">
      <a-input
        :default-value="form.summary"
        :disabled="loading"
        @input="onChangeSummary"
        id="summary"
        placeholder="Geef een titel aan de afspraak"
      />
      <div v-for="message in errors.summary" class="validation-error">{{ message }}</div>
    </a-form-model-item>

    <a-form-model-item label="Locatie">
      <a-radio-group
        :default-value="form.is_visit"
        :disabled="loading"
        @change="onChangeIsVisit"
        class="location-select full-width"
        button-style="solid"
      >
        <a-radio-button class="width-third" :value="CalendarEventVisitTypeEnum.HOME">
          Bij de klant
        </a-radio-button>
        <a-radio-button class="width-third" :value="CalendarEventVisitTypeEnum.OFFICE">
          Op kantoor
        </a-radio-button>
        <a-radio-button class="width-third" :value="null">
          Anders, nl.:
        </a-radio-button>
      </a-radio-group>
    </a-form-model-item>

    <a-form-model-item label=" ">
      <a-input
        v-model="form.location"
        id="location"
        placeholder="Geef een locatie op"
        :disabled="disableLocation"
      />
    </a-form-model-item>

    <a-form-model-item label="Beschrijving">
      <a-textarea
        v-model="form.description"
        :rows="10"
        id="description"
        :disabled="loading"
        placeholder="Indien gewenst kunt u hier een uitgebreidere toelichting van de activiteit geven"
      ></a-textarea>
    </a-form-model-item>

    <a-form-model-item label="Klant">
      <customer-search-by-name-input-component
        @change="onChangeCustomer"
        :value="form.customer_id"
        :disabled="loading"
      />
    </a-form-model-item>

    <a-form-model-item label="Kalender">
      <Skeleton class="skeleton" :loading="true" v-if="loadingCalendars"></Skeleton>
      <a-select
        v-else
        placeholder="Maak een keuze"
        id="calendar_id"
        :disabled="loading"
        :default-value="form.calendar_id ? form.calendar_id : undefined"
        :options="calendars.map(c => ({label: c.name, value: c.id}))"
        @change="onChangeCalendar"
      ></a-select>
      <div v-for="message in errors.calendar_id" class="validation-error">
        {{ message }}
      </div>
    </a-form-model-item>

    <a-form-model-item label="Vanaf">
      <div style="display: flex;" :key="dateTimeKey">
        <date-picker-component
          v-model="form.time_start"
          :block="true"
          :allow-clear="true"
          :format="DateFormatEnum.DISPLAY_DATE"
          :disabled="loading"
          style="flex:auto; margin-right: 10px;"
          @change="onChangeDateOrTime($event, 'time_start')"
        />

        <time-picker-component
          v-model="form.time_start"
          :disabled="disableTimes"
          :format="DateFormatEnum.DISPLAY_TIME"
          :allow-clear="true"
          @change="onChangeDateOrTime($event, 'time_start')"
        />
      </div>
      <div v-for="message in errors.time_start" class="validation-error">
        {{ message }}
      </div>
    </a-form-model-item>

    <a-form-model-item label="Tot">
      <div style="display: flex;" :key="dateTimeKey">
        <date-picker-component
          v-model="form.time_end"
          style="flex:auto; margin-right: 10px;"
          :block="true"
          :allow-clear="true"
          :format="DateFormatEnum.DISPLAY_DATE"
          :disabled="loading"
          :disabled-date="disabledEndDate"
          @change="onChangeDateOrTime($event, 'time_end')"
        />

        <time-picker-component
          v-model="form.time_end"
          :disabled="disableTimes"
          :format="DateFormatEnum.DISPLAY_TIME"
          :allow-clear="true"
          :disabled-hours="disabledEndHours"
          :disabled-minutes="disabledEndMinutes"
          @change="onChangeDateOrTime($event, 'time_end')"
        />
      </div>
      <div v-for="message in errors.time_end" class="validation-error">
        {{ message }}
      </div>
    </a-form-model-item>

    <a-form-model-item label=" ">
      <a-switch @change="onChangeAllDay" :default-checked="form.all_day" :disabled="loading"/>
      <strong> Hele dag?</strong>
    </a-form-model-item>

    <a-form-model-item :wrapper-col="{ span: 16, offset: 6 }">
      <a-button class="cancel-calendar-event" type="secondary" @click="onCancel" :disabled="loading">
        Annuleren
      </a-button>
      <a-button class="save-calendar-event" type="primary" :loading="loading" @click="onSave" :disabled="loading">
        Opslaan
      </a-button>
    </a-form-model-item>

  </a-form-model>
</template>

<script>
import calendarApi from '~/api/calendar';
import calendarEventApi from '~/api/calendar_event';
import customerApi from '~/api/customer';
import customerPropertyApi from '~/api/customer_property';
import officeApi from '~/api/office';
import DatePickerComponent from '~/components/DatePickerComponent';
import TimePickerComponent from '~/components/TimePickerComponent';
import moment from 'moment';
import DateFormatEnum from '~/enums/DateFormatEnum';
import {Skeleton} from 'vue-loading-skeleton';
import CustomerSearchByNameInputComponent from "../../customer/components/SearchByNameInputComponent.vue";
import {CalendarEventVisitTypeEnum, CustomerPropertyAddressTypeEnum} from '~/enums';

export default {
  name: "CreateOrEditCalendarEventComponent",
  components: {
    CustomerSearchByNameInputComponent,
    TimePickerComponent,
    DatePickerComponent,
    Skeleton
  },
  props: {
    event: {
      type: Object,
      required: false,
    },
    customerId: {
      type: [String, Number],
      required: false,
    },
    attachableType: {
      type: String,
      required: false
    },
    attachableKey: {
      type: [String, Number],
      required: false
    },
  },
  data() {
    return {
      errors: {},
      loading: true,
      loaded: false,
      loadingCalendars: true,
      form: {},
      calendars: {},
      DateFormatEnum,
      dateTimeKey: 1,
      disableTimes: false,
      CalendarEventVisitTypeEnum,
      formattedCustomer: null,
    };
  },
  created() {
    this.resetForm();

    this.disableTimes = this.form.all_day;

    calendarApi.getByUser(window.state.user.id).then((response) => {
      this.calendars = response.data;
    }).catch(() => {
      this.$notification['error']({
        message: 'Er ging iets mis',
        description: "Het is niet gelukt de agenda's op te halen"
      });
    }).finally(() => {
      this.loadingCalendars = false;
    });

    // format the customer by this.event.customer or this.customerId
    if (this.event) {
      if (typeof this.event.customer !== 'undefined' && this.event.customer !== null
          && typeof this.event.customer.data !== 'undefined' && this.event.customer.data !== null) {
        const c = this.event.customer.data;
        this.formattedCustomer = `${c.suffix ? c.suffix : ''} ${c.last_name} (${c.first_name}) - ${c.age} jaar`;
      }
    } else if (this.customerId) {
      customerApi.find(this.customerId).then((response) => {
        const c = response.data;
        this.formattedCustomer = `${c.suffix ? c.suffix : ''} ${c.last_name} (${c.first_name}) - ${c.age} jaar`;
      })
    }

    this.loading = false;
    this.loaded = true;
  },
  computed: {
    disableLocation() {
      return this.loading || Object.values(CalendarEventVisitTypeEnum).includes(this.form.is_visit);
    },
  },
  methods: {
    onChangeSummary(event) {
      const summary = {'summary': event.target.value};
      Object.assign(this.form, summary);
      if (this.errors.summary && event.target.value !== '') {
        this.$delete(this.errors, 'summary');
      }
    },
    async onChangeIsVisit(event) {
      this.form.is_visit = event.target.value;

      switch (event.target.value) {
        case CalendarEventVisitTypeEnum.HOME:
          this.form.location = await this.getCustomerAddress();
          break;
        case CalendarEventVisitTypeEnum.OFFICE:
          this.form.location = await this.getOfficeAddress();
          break;
        default:
          this.form.location = null;
          break;
      }
    },
    async getCustomerAddress() {
      return customerPropertyApi.findCurrentByCustomerAndAddressType(
          this.form.customer_id,
          CustomerPropertyAddressTypeEnum.VISIT,
          ['property']
      ).then((response) => {
        const customer_property = response.data;
        if (customer_property.property !== null && typeof customer_property.property !== "undefined"
            && customer_property.property.data !== null && typeof customer_property.property.data !== "undefined") {
          const property = customer_property.property.data;
          return this.formatAddress([
            property.street || null,
            property.number || null,
            property.number_letter || null,
            property.number_addition || null,
            property.postal_code || null,
            property.city || null
          ]);
        } else {
          return 'Adres van klant onbekend';
        }
      }).catch((error) => {
        this.$notification['error']({
          message: 'Er ging iets mis',
          description: 'Het is niet gelukt om het klantadres op te halen',
        });
        return 'Adres van klant onbekend';
      })
    },
    async getOfficeAddress() {
      return officeApi.findByCustomer(this.form.customer_id).then((response) => {
        const office = response.data;
        return this.formatAddress([
          office.streetName || null,
          office.houseNumber || null,
          office.houseNumberAddition || null,
          office.postcode || null,
          office.city || null
        ]);
      }).catch((error) => {
        this.$notification['error']({
          message: 'Er ging iets mis',
          description: 'Het is niet gelukt om het kantooradres op te halen',
        });
        return 'Adres van kantoor onbekend';
      });
    },
    formatAddress(addressArray) {
      return addressArray.filter(val => val !== null).join(' ');
    },
    onChangeCustomer(value) {
      const customer = {'customer_id': value};
      Object.assign(this.form, customer);
    },
    onChangeCalendar(value) {
      this.form.calendar_id = value;
      if (this.errors && this.errors.calendar_id) {
        this.$delete(this.errors, 'calendar_id');
      }
    },
    onChangeDateOrTime(datetime, key) {
      let value = {};
      value[key] = datetime?.format();

      // when changing time_start, time_end changes with same time difference
      if (key === "time_start" && datetime !== null) {
        let difference = moment(datetime).diff(moment(this.form[key]));

        let endValue = {};
        endValue["time_end"] = moment(this.form["time_end"]).add(difference).format();

        Object.assign(this.form, endValue);
      }

      Object.assign(this.form, value);

      if (this.errors[key] && (this.form.time_end > this.form.time_start)) {
        this.$delete(this.errors, key);
      }

      this.dateTimeKey += 1;
    },
    onChangeAllDay(value) {
      if (value) {
        this.onChangeDateOrTime(moment(this.form.time_start, DateFormatEnum.ISO8601)
            .set({'hour': 8, 'minute': 0, 'second': 0}), 'time_start');
        this.onChangeDateOrTime(moment(this.form.time_end, DateFormatEnum.ISO8601)
            .set({'hour': 17, 'minute': 0, 'second': 0}), 'time_end');
        this.form.all_day = true;
        this.disableTimes = true;
      } else {
        this.form.all_day = false;
        this.disableTimes = false;
      }
    },
    onSave() {
      this.loading = true;
      this.$refs.calendarEventForm.validate(valid => {
        if (valid) {
          if (!Object.values(CalendarEventVisitTypeEnum).includes(this.form.is_visit)) {
            this.form.is_visit = null;
          }

          if (this.event && this.event.id) {
            calendarEventApi.update(this.form).then(() => {
              this.$emit('updated', this.form);
              this.$notification['success']({
                message: 'Opgeslagen',
                description: 'De afspraak is gewijzigd'
              })
            }).catch((errors) => {
              this.errors = errors.response.data.error.errors;
              this.$notification['error']({
                message: 'Er ging iets mis',
                description: 'Het is niet gelukt de afspraak aan te passen'
              });
            }).finally(() => {
              this.loading = false;
            });
          } else {
            calendarEventApi.store(this.form).then(() => {
              this.$emit('updated', this.form);
              this.$notification['success']({
                message: 'Opgeslagen',
                description: 'De afspraak is aangemaakt',
              })
            }).catch((errors) => {
              this.errors = errors.response.data.error.errors;
              this.$notification['error']({
                message: 'Er ging iets mis',
                description: 'Het is niet gelukt de afspraak op te slaan',
              })
            }).finally(() => {
              this.loading = false;
            });
          }
        } else {
          this.loading = false;
          return false;
        }
      })
    },
    resetForm() {
      this.form = {
        all_day: this.event ? this.event.all_day : false,
        attachable_key: this.event ? this.event.attachable_key ?? null : this.attachableKey ?? null,
        attachable_type: this.event ? this.event.attachable_type ?? null : this.attachableType ?? null,
        calendar_id: this.event ? this.event.calendar_id : null,
        description: this.event ? this.event.description : null,
        customer_id: this.event ? this.event.customer_id : this.customerId ?? null,
        event_type: this.event ? this.event.event_type : null,
        id: this.event ? this.event.id : null,
        is_visit: this.event ? this.event.is_visit : null,
        location: this.event ? this.event.location : null,
        summary: this.event ? this.event.summary : null,
        time_start: this.event ? this.event.time_start : moment().format(DateFormatEnum.ISO8601),
        time_end: this.event ? this.event.time_end : moment().add(1, 'hours').format(DateFormatEnum.ISO8601),
      };
    },
    onCancel() {
      this.$refs.calendarEventForm.resetFields();
      this.resetForm();
      this.$emit('close');
    },

    disabledEndDate(current) {
      return current <= moment(this.form.time_start);
    },
    disabledEndHours(current) {
      // TODO: set disabledHours
    },
    disabledEndMinutes(current) {
      // TODO: set disabledMinutes
    },
  },
};
</script>

<style scoped>
.cancel-calendar-event {
  margin-right: 10px;
  width: calc(50% - 5px);
}

.save-calendar-event {
  width: calc(50% - 5px);
}

.full-width {
  width: 100%;
}

.width-third {
  width: calc(100% / 3);
}

.skeleton {
  border-color: rgb(217, 217, 217);
  border-radius: 4px;
  height: 32px;
  width: 100%;
}

.validation-error {
  font-style: italic;
  color: red;
  text-align: right;
}
</style>
